/**
 * 📋 FOLLOW-UP SERVICE - Manage follow-up tasks for users
 */

import { getApiBaseUrl } from '@/config/environment';


export interface FollowUpItem {
  id: string;
  userId: string;
  type: 'verification_pending' | 'incomplete_onboarding' | 'no_first_login' | 'pending_host_approval' | 'driver' | 'host' | 'custom';
  title: string;
  description: string;
  urgency: 'low' | 'medium' | 'high';
  status?: 'open' | 'in_progress' | 'resolved';
  userInfo: {
    email: string;
    phone: string;
    firstName?: string;
    lastName?: string;
    name: string;
  };
  metadata?: {
    completionPercentage?: number;
    daysPending?: number;
    daysInactive?: number;
    pendingVehicles?: number;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string;
  notes?: FollowUpNote[];
  assignedTo?: string;
}

export interface FollowUpNote {
  id: string;
  content: string;
  author: string;
  timestamp: string;
  type: 'call' | 'email' | 'meeting' | 'system';
}

export interface FollowUpSummary {
  total: number;
  byUrgency: {
    high: number;
    medium: number;
    low: number;
  };
  byType: {
    verification_pending: number;
    incomplete_onboarding: number;
    no_first_login: number;
    pending_host_approval: number;
  };
  byStatus?: {
    open: number;
    in_progress: number;
    resolved: number;
  };
}

export interface FollowUpResponse {
  success: boolean;
  data: {
    followUps: FollowUpItem[];
    summary: FollowUpSummary;
  };
  timestamp: string;
}

export const followUpService = {
  /**
   * 📋 Get all follow-up tasks
   */
  async getFollowUps(filters?: {
    urgency?: 'low' | 'medium' | 'high';
    type?: string;
    status?: 'open' | 'in_progress' | 'resolved';
  }): Promise<{
    success: boolean;
    data?: {
      followUps: FollowUpItem[];
      summary: FollowUpSummary;
    };
    error?: string;
  }> {
    try {

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/follow-ups`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FollowUpResponse = await response.json();

      // Get custom follow-ups from localStorage
      const customFollowUps = this.getCustomFollowUps();

      // Merge API follow-ups with custom ones
      const allFollowUps = [
        ...result.data.followUps.map(item => ({
          ...item,
          status: item.status || 'open' as 'open'
        })),
        ...customFollowUps
      ];

      // Apply filters if provided
      let filteredFollowUps = allFollowUps;
      if (filters?.urgency) {
        filteredFollowUps = filteredFollowUps.filter(f => f.urgency === filters.urgency);
      }
      if (filters?.type) {
        filteredFollowUps = filteredFollowUps.filter(f => f.type === filters.type);
      }
      if (filters?.status) {
        filteredFollowUps = filteredFollowUps.filter(f => f.status === filters.status);
      }

      // Update summary with custom items
      const summary: FollowUpSummary = {
        total: allFollowUps.length,
        byUrgency: {
          high: allFollowUps.filter(f => f.urgency === 'high').length,
          medium: allFollowUps.filter(f => f.urgency === 'medium').length,
          low: allFollowUps.filter(f => f.urgency === 'low').length
        },
        byType: {
          ...result.data.summary.byType,
          // Custom types won't be counted in the API summary
        },
        byStatus: {
          open: allFollowUps.filter(f => f.status === 'open').length,
          in_progress: allFollowUps.filter(f => f.status === 'in_progress').length,
          resolved: allFollowUps.filter(f => f.status === 'resolved').length
        }
      };

      return {
        success: true,
        data: {
          followUps: filteredFollowUps,
          summary
        }
      };
    } catch (error) {
      console.error('❌ Error fetching follow-ups:', error);

      // Fallback to custom follow-ups only
      const customFollowUps = this.getCustomFollowUps();

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch follow-ups',
        data: {
          followUps: customFollowUps,
          summary: {
            total: customFollowUps.length,
            byUrgency: {
              high: customFollowUps.filter(f => f.urgency === 'high').length,
              medium: customFollowUps.filter(f => f.urgency === 'medium').length,
              low: customFollowUps.filter(f => f.urgency === 'low').length
            },
            byType: {
              verification_pending: 0,
              incomplete_onboarding: 0,
              no_first_login: 0,
              pending_host_approval: 0
            },
            byStatus: {
              open: customFollowUps.filter(f => f.status === 'open').length,
              in_progress: customFollowUps.filter(f => f.status === 'in_progress').length,
              resolved: customFollowUps.filter(f => f.status === 'resolved').length
            }
          }
        }
      };
    }
  },

  /**
   * ➕ Create a new custom follow-up (stored locally)
   * TODO: Implement backend endpoint POST /api/v1/admin/follow-ups
   */
  async createFollowUp(followUp: Omit<FollowUpItem, 'id' | 'createdAt' | 'status'>): Promise<{
    success: boolean;
    data?: FollowUpItem;
    error?: string;
  }> {
    try {
      const newFollowUp: FollowUpItem = {
        ...followUp,
        id: `custom-${Date.now()}`,
        status: 'open',
        createdAt: new Date().toISOString(),
        notes: []
      };

      // Store in localStorage for now
      const customFollowUps = this.getCustomFollowUps();
      customFollowUps.push(newFollowUp);
      localStorage.setItem('customFollowUps', JSON.stringify(customFollowUps));


      return {
        success: true,
        data: newFollowUp
      };
    } catch (error) {
      console.error('❌ Error creating follow-up:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create follow-up'
      };
    }
  },

  /**
   * ✅ Resolve/close a follow-up
   * TODO: Implement backend endpoint PATCH /api/v1/admin/follow-ups/:id
   */
  async resolveFollowUp(id: string, notes?: string): Promise<{
    success: boolean;
    data?: FollowUpItem;
    error?: string;
  }> {
    try {
      // Check if it's a custom follow-up
      if (id.startsWith('custom-')) {
        const customFollowUps = this.getCustomFollowUps();
        const index = customFollowUps.findIndex(f => f.id === id);

        if (index !== -1) {
          customFollowUps[index].status = 'resolved';
          customFollowUps[index].resolvedAt = new Date().toISOString();
          customFollowUps[index].updatedAt = new Date().toISOString();

          if (notes) {
            const note: FollowUpNote = {
              id: Date.now().toString(),
              content: notes,
              author: 'Current User',
              timestamp: new Date().toISOString(),
              type: 'system'
            };
            customFollowUps[index].notes = [...(customFollowUps[index].notes || []), note];
          }

          localStorage.setItem('customFollowUps', JSON.stringify(customFollowUps));


          return {
            success: true,
            data: customFollowUps[index]
          };
        }
      }

      // For API follow-ups, we'll store the resolved status locally for now
      // TODO: Implement backend PATCH endpoint
      const resolvedItems = JSON.parse(localStorage.getItem('resolvedFollowUps') || '{}');
      resolvedItems[id] = {
        resolvedAt: new Date().toISOString(),
        notes
      };
      localStorage.setItem('resolvedFollowUps', JSON.stringify(resolvedItems));


      return {
        success: true
      };
    } catch (error) {
      console.error('❌ Error resolving follow-up:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve follow-up'
      };
    }
  },

  /**
   * 📝 Add a note to a follow-up
   */
  async addNote(id: string, note: Omit<FollowUpNote, 'id' | 'timestamp'>): Promise<{
    success: boolean;
    data?: FollowUpNote;
    error?: string;
  }> {
    try {
      const newNote: FollowUpNote = {
        ...note,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };

      // For custom follow-ups
      if (id.startsWith('custom-')) {
        const customFollowUps = this.getCustomFollowUps();
        const index = customFollowUps.findIndex(f => f.id === id);

        if (index !== -1) {
          customFollowUps[index].notes = [...(customFollowUps[index].notes || []), newNote];
          customFollowUps[index].updatedAt = new Date().toISOString();
          localStorage.setItem('customFollowUps', JSON.stringify(customFollowUps));
        }
      } else {
        // Store notes for API follow-ups locally
        const followUpNotes = JSON.parse(localStorage.getItem('followUpNotes') || '{}');
        if (!followUpNotes[id]) {
          followUpNotes[id] = [];
        }
        followUpNotes[id].push(newNote);
        localStorage.setItem('followUpNotes', JSON.stringify(followUpNotes));
      }


      return {
        success: true,
        data: newNote
      };
    } catch (error) {
      console.error('❌ Error adding note:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to add note'
      };
    }
  },

  /**
   * 📂 Get custom follow-ups from localStorage
   */
  getCustomFollowUps(): FollowUpItem[] {
    try {
      const stored = localStorage.getItem('customFollowUps');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  /**
   * 📊 Get notes for a follow-up
   */
  getNotesFor(id: string): FollowUpNote[] {
    try {
      // For custom follow-ups
      if (id.startsWith('custom-')) {
        const customFollowUps = this.getCustomFollowUps();
        const followUp = customFollowUps.find(f => f.id === id);
        return followUp?.notes || [];
      }

      // For API follow-ups
      const followUpNotes = JSON.parse(localStorage.getItem('followUpNotes') || '{}');
      return followUpNotes[id] || [];
    } catch {
      return [];
    }
  }
};
